"use client";

import { MessageContent, MessageActions } from "./message";
import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import { Sparkles, User, ShoppingBag } from "lucide-react";
import * as React from "react";
import { MessageGenerationStage } from "./message-generation-stage";
import { useTranslation } from 'react-i18next';

/**
 * Thread Content component
 *
 * This component renders the messages in a chat thread.
 * It automatically handles scrolling and loading states.
 */

export interface ThreadContentProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "solid";
  onPreviewImage?: (url: string) => void;
}

export function ThreadContent({ className, variant = "default", onPreviewImage, ...props }: ThreadContentProps) {
  const { t, i18n } = useTranslation();
  const { thread, isWaiting, isStreaming, sendMessage } = useCartAssistant();
  
  const messages = React.useMemo(() => {
    const rawMsgs = thread?.thread?.messages ?? [];
    if (rawMsgs.length === 0) return [];

    // Combine original index with timestamp for stable chronological sorting.
    // Defaulting missing timestamps (e.g., initial system messages) to 0 
    // ensures they stay at the top of the list as foundational context.
    return [...rawMsgs].sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      
      if (timeA !== timeB) return timeA - timeB;
      
      // Fallback to original array order for identical or missing timestamps
      return rawMsgs.indexOf(a) - rawMsgs.indexOf(b);
    });
  }, [thread?.thread?.messages]);

  // Helper to check if a message has any visible content
  const hasVisibleContent = React.useCallback((message: any) => {
    if (!message.content) return false;
    
    if (Array.isArray(message.content)) {
      return message.content.some((c: any) => {
        if (c.type === 'text') return c.text && c.text.trim() !== "" && !c.text.includes('[TERMINAL_USER_APPROVAL]');
        // Always return true for components and resources so they show in history
        if (c.type === 'component' || c.type === 'resource') return true; 
        return false;
      });
    }
    
    const text = typeof message.content === 'string' ? (message.content as string) : "";
    return text.trim() !== "" && !text.includes('[TERMINAL_USER_APPROVAL]');
  }, []);

  const lastMessage = messages[messages.length - 1];
  const lastMessageHasContent = lastMessage ? hasVisibleContent(lastMessage) : false;
  const isLastAssistant = lastMessage?.role === "assistant";
  
  const showThinkingIndicator = (isWaiting || isStreaming) && (
    messages.length === 0 || 
    lastMessage?.role === 'user' || 
    (isLastAssistant && !lastMessageHasContent)
  );

  const visibleMessages = messages.filter(m => {
    // 1. Filter out explicit system messages
    if (m.role === 'system') return false;
    
    // 2. Hide any message that looks like a prompt or diagnostic marker
    const content = typeof m.content === 'string' 
      ? m.content 
      : (Array.isArray(m.content) 
          ? m.content.map(c => (c as any)?.text || '').join(' ') 
          : '');
    
    const safeContent = (content || '').trim();
    if (!safeContent && !m.content?.some?.((c: any) => c.type === 'component')) return false;

    // EXTREME FILTER: Hide any message that contains core system prompt headers or identifies as the persona
    if (
      safeContent.includes('You are a CartLens Sidekick') || 
      safeContent.includes('VISUAL INTELLIGENCE MANDATORY') || 
      safeContent.includes('PRODUCT SIGNATURE RULE') ||
      safeContent.includes('// YOUR TASK') ||
      safeContent.includes('IMAGE-TO-BASKET WORKFLOW') ||
      safeContent.includes('IDENTITY RESOLUTION PROTOCOL') ||
      safeContent.includes('MANDATORY FIELDS') ||
      safeContent.includes('CLEAN OUTPUT: NEVER leak') ||
      safeContent.includes('[TERMINAL_USER_APPROVAL]')
    ) return false;

    return hasVisibleContent(m);
  });

  if (visibleMessages.length === 0 && !showThinkingIndicator) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-6 animate-in fade-in zoom-in-95 duration-700">
        <div className="relative">
          <div className="w-20 h-20 bg-emerald-50 ring-1 ring-emerald-100 rounded-[2rem] flex items-center justify-center overflow-hidden p-3">
            <img src="/icon-128x128.png" alt="Assistant" className="w-full h-full object-contain" />
          </div>
        </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-black text-[#1a2b3c] tracking-tight">{t('chat.assistant')}</h3>
                    <p className="text-[13px] font-medium text-gray-400 max-w-[260px] leading-relaxed">
                      {t('chat.ready_to_optimize')}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-2 w-full max-w-[280px] mt-4">
                    {[t('chat.suggestion_basket'), t('chat.suggestion_stores'), t('chat.suggestion_history')].map((text) => (
                      <button
                        key={text}
                        type="button"
                        onClick={() => sendMessage(text)}
                        className="px-4 py-3 rounded-2xl bg-white border border-gray-200 text-[12px] font-semibold text-[#1a2b3c] hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left"
                      >
                        {text}
                      </button>
                    ))}
                  </div>
        
      </div>
    );
  }

  return (
    <div
      className={cn("flex-1 px-4 py-5 sm:px-5 flex flex-col gap-5", className)}
      {...props}
    >
      {visibleMessages.map((message, idx) => {
        const isLastInThread = idx === visibleMessages.length - 1;
        const isMessageStreaming = isLastInThread && isStreaming;
        
        // Use the actual message creation time for display
        const displayTime = (message as any).createdAt ? new Date((message as any).createdAt).getTime() : undefined;

        return (
          <div
            key={message.id}
            className={cn(
              "flex flex-col w-full gap-1.5 animate-in fade-in slide-in-from-bottom-1 duration-300",
              message.role === "user" ? "items-end" : "items-start"
            )}
          >
            <div className="w-full">
              <MessageContent 
                message={message} 
                isLoading={isMessageStreaming}
                role={message.role}
                onPreviewImage={onPreviewImage}
              />
              <MessageActions message={message}>
                {displayTime && (
                  <span className={cn(
                    "text-[10px] font-mono text-gray-400 tabular-nums",
                    message.role === "user" ? "text-right" : "text-left"
                  )}>
                    {new Date(displayTime).toDateString() === new Date().toDateString()
                      ? new Date(displayTime).toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' })
                      : new Date(displayTime).toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
              </MessageActions>
            </div>
          </div>
        );
      })}
      
      {showThinkingIndicator && (
        <div className="flex flex-col items-start gap-3 animate-in fade-in slide-in-from-bottom-1 duration-300 pb-2">
          <MessageGenerationStage showLabel={true} />
        </div>
      )}
    </div>
  );
}
